import { useCallback, useEffect, useMemo, useState } from 'react';
import { db, auth } from '../firebase';
import {
  fetchActiveAlertsForUser,
  isAlertDismissed,
  isAlertRead,
  subscribeActiveAlertsForUser,
} from '../lib/userAlerts';
import { useWorkspace } from '../context/WorkspaceContext';
import { checkUserPermission } from '../lib/rolePermissions';
import { filterAlertsByModuleAccess } from '../lib/alertModuleAccess';
import {
  INBOX_KIND_ALERT,
  inboxItemSortTime,
  isAnnouncementSeen,
  isInboxItemAnnouncement,
  isInboxItemRead,
} from '../lib/announcements';
import { useCompanyAnnouncements } from './useCompanyAnnouncements';

/**
 * Shared company-scoped alerts + announcements list for inbox UI + toolbar badge.
 * @param {{ skip?: boolean }} [options] — when skip is true, do not fetch (parent supplies data via alertsRemote)
 */
export function useCompanyAlerts(userData, options = {}) {
  const skip = options.skip === true;
  const [alertRows, setAlertRows] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(!skip);
  const uid = auth.currentUser?.uid;
  const { workspace } = useWorkspace();

  const {
    announcements,
    loading: announcementsLoading,
    refresh: refreshAnnouncements,
  } = useCompanyAnnouncements(userData, { skip });

  const resolvedUserData = useMemo(() => {
    if (!userData) return null;
    if (!uid) return userData;
    return { ...userData, uid };
  }, [userData, uid]);

  const checkPermission = useCallback(
    (moduleName, action) =>
      checkUserPermission(resolvedUserData?.role, workspace, moduleName, action),
    [resolvedUserData?.role, workspace],
  );

  const applyModuleFilter = useCallback(
    (list) => filterAlertsByModuleAccess(list, resolvedUserData, checkPermission),
    [resolvedUserData, checkPermission],
  );

  const refreshAlertsOnly = useCallback(async () => {
    if (skip) return;
    if (!resolvedUserData) {
      setAlertRows([]);
      setAlertsLoading(false);
      return;
    }
    setAlertsLoading(true);
    try {
      const list = await fetchActiveAlertsForUser(db, resolvedUserData);
      setAlertRows(list);
    } catch (e) {
      console.error('useCompanyAlerts:', e);
      setAlertRows([]);
    } finally {
      setAlertsLoading(false);
    }
  }, [resolvedUserData, skip]);

  const refresh = useCallback(async () => {
    await Promise.all([refreshAlertsOnly(), refreshAnnouncements()]);
  }, [refreshAlertsOnly, refreshAnnouncements]);

  useEffect(() => {
    if (skip) {
      setAlertRows([]);
      setAlertsLoading(false);
      return undefined;
    }
    if (!resolvedUserData) {
      setAlertRows([]);
      setAlertsLoading(false);
      return undefined;
    }

    setAlertsLoading(true);
    const unsubscribe = subscribeActiveAlertsForUser(
      db,
      resolvedUserData,
      (list) => {
        setAlertRows(list);
        setAlertsLoading(false);
      },
      () => {
        setAlertRows([]);
        setAlertsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [resolvedUserData, skip]);

  const rows = useMemo(() => {
    const alerts = applyModuleFilter(alertRows).map((row) => ({
      ...row,
      inboxKind: INBOX_KIND_ALERT,
    }));
    return [...alerts, ...announcements].sort(
      (a, b) => inboxItemSortTime(b) - inboxItemSortTime(a),
    );
  }, [alertRows, announcements, applyModuleFilter]);

  const loading = alertsLoading || announcementsLoading;

  const unreadInInbox = useMemo(() => {
    if (!uid) return 0;
    return rows.filter((row) => {
      if (isInboxItemAnnouncement(row)) {
        return !isAnnouncementSeen(row, uid);
      }
      return !isAlertDismissed(row, uid) && !isAlertRead(row, uid);
    }).length;
  }, [rows, uid]);

  return { rows, loading, refresh, unreadInInbox, isInboxItemRead };
}
